import { isEqual } from 'lodash';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  useGetProductUnitsListsQuery,
  useLazyGetProductUnitsSearchResultQuery,
  useSearchProductUnitsMutation,
} from '@app/api/Products';
import type {
  GetProductUnitsSearchResultRequest,
  ModelProductUnit,
  ModelProductUnitsSearchResult,
  SearchProductUnitsRequest,
} from '@app/api/Products';
import { useUserPrint } from '@app/hooks';
import {
  getGoodsUnitsColumns,
  initialFilters,
  initPagination,
} from '@app/pages/Warehouses/GoodsUnits/data';
import type {
  UnitsFilters,
  Pagination,
  FilterChangeType,
} from '@app/pages/Warehouses/GoodsUnits/types';
import { getOptions } from '@app/pages/Warehouses/utils';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { useErrorHandler } from '@app/shared/hooks';
import type { Options } from '@app/types';

import type { PaginationProps } from 'antd';

export const useGoodsUnits = () => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { showNotification } = useNotification();
  const {
    params: { ActiveBranchId },
  } = useUserPrint();

  const {
    data: filtersLists,
    isFetching: isFiltersListsFetching,
    isLoading: isFiltersListsLoading,
    isUninitialized: isFiltersListsUninitialized,
    isError: isFiltersListsError,
    error: filtersListsError,
  } = useGetProductUnitsListsQuery(
    { ActiveBranchId },
    {
      skip: !ActiveBranchId,
      refetchOnMountOrArgChange: true,
      refetchOnReconnect: true,
    }
  );

  useErrorHandler([{ flag: isFiltersListsError, error: filtersListsError }]);

  const [getProductUnitsSearchResult, { isLoading: isLoadingById }] =
    useLazyGetProductUnitsSearchResultQuery();

  const [searchProductUnits, { isLoading, isUninitialized }] =
    useSearchProductUnitsMutation();

  const [searchProductUnitsData, setSearchProductUnitsData] =
    useState<ModelProductUnitsSearchResult | null>(null);

  const searchIdRef = useRef<number | null>(null);
  const lastFiltersRef = useRef<UnitsFilters | null>(null);
  const lastBranchRef = useRef(ActiveBranchId);

  const [statesOptions, setStatesOptions] = useState<Options>([]);
  const [productsOptions, setProductsOptions] = useState<Options>([]);
  const [warehousesOptions, setWarehousesOptions] = useState<Options>([]);

  const [filters, setFilters] = useState<UnitsFilters>(() => ({
    ...initialFilters,
  }));
  const [unsavedFilters, setUnsavedFilters] = useState<UnitsFilters>(() => ({
    ...initialFilters,
  }));

  const [pagination, setPagination] = useState<Pagination>(
    () => initPagination
  );

  const onFilterChange: FilterChangeType = useCallback((key, value) => {
    setUnsavedFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const skipFilters = useCallback(() => {
    setUnsavedFilters(initialFilters);
    setFilters(initialFilters);
  }, []);

  const onSearch = useCallback(() => {
    setPagination((prev) => ({ ...prev, PageN: 1 }));
    setFilters(unsavedFilters);
  }, [unsavedFilters]);

  const onPaginate: PaginationProps['onChange'] = useCallback(
    (PageN: number) => {
      setPagination((prev) => ({ ...prev, PageN }));
    },
    []
  );

  const onPageSizeChange = useCallback((ItemsPerPage: number) => {
    setPagination((p) => ({ ...p, ItemsPerPage, PageN: 1 }));
  }, []);

  const isPageLoading = useMemo(
    () => (isLoading || isLoadingById) && !isUninitialized,
    [isLoading, isLoadingById, isUninitialized]
  );
  const isFetching = useMemo(
    () => isLoading || isLoadingById,
    [isLoading, isLoadingById]
  );

  const disabledFilters = useMemo(() => {
    return (
      isFiltersListsFetching ||
      isFiltersListsLoading ||
      isFiltersListsUninitialized ||
      isFetching ||
      isPageLoading
    );
  }, [
    isFiltersListsFetching,
    isFiltersListsLoading,
    isFiltersListsUninitialized,
    isFetching,
    isPageLoading,
  ]);

  const tableData = useMemo(() => {
    if (!searchProductUnitsData) return [];

    const { ProductUnits } = searchProductUnitsData;

    if (!ProductUnits || !ProductUnits.length) return [];

    return ProductUnits;
  }, [searchProductUnitsData]);

  const { PageN, ItemsPerPage, Total } = useMemo(
    () => pagination,
    [pagination]
  );

  const searchProductUnitsHandler = useCallback(
    async (filters: UnitsFilters, pagination: Pagination) => {
      const data: SearchProductUnitsRequest['data'] = {
        ...filters,
        ...pagination,
      };
      try {
        const response = await searchProductUnits({
          data,
          ActiveBranchId,
        }).unwrap();

        setSearchProductUnitsData(response);

        searchIdRef.current = response.Id;
        lastFiltersRef.current = filters;
        lastBranchRef.current = ActiveBranchId;

        const { CountProductUnits } = response;
        setPagination((prev) => ({ ...prev, Total: CountProductUnits }));
      } catch (error) {
        showNotification(error, 'error');
      }
    },
    [ActiveBranchId, searchProductUnits, showNotification]
  );

  const getProductUnitsSearchResultHandler = useCallback(
    async (params: GetProductUnitsSearchResultRequest) => {
      try {
        const response = await getProductUnitsSearchResult(params).unwrap();
        setSearchProductUnitsData(response);
        const { CountProductUnits } = response;
        setPagination((prev) => ({ ...prev, Total: CountProductUnits }));
      } catch (error) {
        showNotification(error, 'error');
      }
    },
    [getProductUnitsSearchResult, showNotification]
  );

  const onSearchHandler = useCallback(async () => {
    const filtersChanged =
      !lastFiltersRef.current || !isEqual(filters, lastFiltersRef.current);

    const branchChanged = ActiveBranchId !== lastBranchRef.current;
    const searchId = searchIdRef.current;

    const isNewSearch = !searchId || filtersChanged || branchChanged;

    if (isNewSearch) {
      if (!filtersLists) return;

      await searchProductUnitsHandler(filters, { PageN, ItemsPerPage, Total });
    } else {
      if (!searchId) {
        showNotification('Не удалось найти идентификатор поиска', 'error');
        return;
      }

      await getProductUnitsSearchResultHandler({
        searchId: searchId,
        pageN: PageN,
        itemsPerPage: ItemsPerPage,
        ActiveBranchId,
      });
    }
  }, [
    filters,
    filtersLists,
    ActiveBranchId,
    searchProductUnitsHandler,
    getProductUnitsSearchResultHandler,
    showNotification,
    PageN,
    ItemsPerPage,
    Total,
  ]);

  const filtersListsHandler = useCallback(() => {
    if (!filtersLists) return;
    if (isInitialized) return;

    const { Products, States, Warehouses } = filtersLists;
    const selectedProductId = Number(searchParams.get('Products'));
    const selectedWarehouseId = Number(searchParams.get('warehouseId'));

    const productsOptions: Options = getOptions(Products);
    const statesOptions: Options = getOptions(States, 'IdS');
    const warehousesOptions: Options = getOptions(Warehouses);

    const hasSelectedProducts =
      productsOptions?.find(
        ({ value }) => Number(value) === selectedProductId
      ) !== undefined;

    const hasSelectedWarehouse =
      warehousesOptions?.find(
        ({ value }) => Number(value) === selectedWarehouseId
      ) !== undefined;
    setProductsOptions(productsOptions);
    setStatesOptions(statesOptions);
    setWarehousesOptions(warehousesOptions);

    setFilters((prev) => ({
      ...prev,
      ProductIds: hasSelectedProducts ? [selectedProductId] : [],
      WarehouseIds: hasSelectedWarehouse ? [selectedWarehouseId] : undefined,
    }));

    setUnsavedFilters((prev) => ({
      ...prev,
      ProductIds: hasSelectedProducts ? [selectedProductId] : [],
      WarehouseIds: hasSelectedWarehouse ? [selectedWarehouseId] : undefined,
    }));

    const params = new URLSearchParams(searchParams);
    params.delete('Products');
    params.delete('warehouseId');
    setSearchParams(params);

    setIsInitialized(true);
  }, [filtersLists, searchParams, setSearchParams, isInitialized]);

  const [isOpenEditSalePriceModal, setIsOpenEditSalePriceModal] =
    useState<boolean>(false);
  const [editSalePriceProductUnit, setEditSalePriceProductUnit] =
    useState<ModelProductUnit | null>(null);

  const onEditSalePrice = useCallback((productUnit: ModelProductUnit) => {
    setEditSalePriceProductUnit(productUnit);
    setIsOpenEditSalePriceModal(true);
  }, []);

  const onCloseEditSalePriceModal = useCallback(() => {
    setIsOpenEditSalePriceModal(false);
    setEditSalePriceProductUnit(null);
  }, []);

  const columns = useMemo(
    () => getGoodsUnitsColumns({ onEditSalePrice }),
    [onEditSalePrice]
  );

  return {
    statesOptions,
    productsOptions,
    warehousesOptions,
    columns,
    tableData,
    isPageLoading,
    isFetching,
    filters,
    unsavedFilters,
    pagination,
    disabledFilters,
    onFilterChange,
    skipFilters,
    onSearch,
    onPaginate,
    onPageSizeChange,
    searchProductUnitsHandler,
    getProductUnitsSearchResultHandler,
    onSearchHandler,
    filtersListsHandler,
    isOpenEditSalePriceModal,
    editSalePriceProductUnit,
    onCloseEditSalePriceModal,
  };
};
